<template>
  <router-link v-if="to" :to="toObject" :tag="tag" :class="classes"
               :disabled="disabled">
    <BrandIcon class="icon" :name="icon" v-if="icon"></BrandIcon>
    <div class="buttonLabel" v-if="label">{{ label }}</div>
    <slot v-if="!label"></slot>
  </router-link>
  <button v-else-if="isButton" :class="classes" @click="clicked" :disabled="disabled">
    <BrandIcon class="icon" :name="icon" v-if="icon"></BrandIcon>
    <div class="buttonLabel" v-if="label">{{ label }}</div>
    <slot v-if="!label"></slot>
  </button>
  <span v-else class="button primary rev" @click="clicked" :disabled="disabled">
    <BrandIcon :class="classes" :name="icon" v-if="icon"></BrandIcon>
    <div class="buttonLabel" v-if="label">{{ label }}</div>
    <slot v-if="!label"></slot>
  </span>
</template>

<script setup lang="ts">
import { computed, ComputedRef, Slots, useSlots } from 'vue';
import { RouteLocationRaw } from 'vue-router';

import BrandIcon from '@/components/icons/BrandIcon.vue';
import { isSlotPopulated } from '@/data/helpers/ComponentHelper';

interface Props {
  icon?: string;
  label?: string;
  tag?: string;
  to?: RouteLocationRaw;
  disabled?: boolean;
  isButton?: boolean;
  iconOnLeft?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  tag: 'button',
  disabled: false,
  isButton: true,
  iconOnLeft: false,
});

interface Emits {
  (e: 'click', event: Event): void
}
const emit = defineEmits<Emits>();

const slots: Slots = useSlots();

const classes: ComputedRef<string> = computed(() => {
  const classes: string[] = ['button', 'primary'];
  if (!props.iconOnLeft) {
    classes.push('rev');
  }
  if (!props.label) {
    if (!isSlotPopulated(slots.default)) {
      classes.push('iconOnly');
    }
  }
  return classes.join(' ');
});

const toObject: ComputedRef<RouteLocationRaw> = computed(() => {
  return props.to ? props.to : {};
});

function clicked(event: MouseEvent): void {
  if (!props.disabled) {
    emit('click', event);
  }
}
</script>

<style scoped lang="scss">
@import '~@/assets/styles/forms/_baseButtonStyles.scss';
</style>
